/**
 * Authentication context for direct login via backend API.
 *
 * Hybrid approach:
 * - HTTP-only cookies store refresh token (XSS protection)
 * - Access token stored in memory for API calls
 * - Automatic token refresh before expiration
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
// Import the carrier from its leaf module (not the analytics barrel) to avoid a
// cycle: AnalyticsProvider imports AuthContext, so AuthContext must not pull in
// the barrel that re-exports AnalyticsProvider. signInMethod.ts has no
// back-dependency on AuthContext.
import { recordSignInMethod } from '@/common/analytics/signInMethod'
import {
  login as apiLogin,
  googleLogin as apiGoogleLogin,
  magicLinkLogin as apiMagicLinkLogin,
  logout as apiLogout,
  refreshToken as apiRefreshToken,
  getCurrentSession,
  AuthServiceError,
  type UserInfo,
} from '@/common/services/auth'

/**
 * Authentication context value
 */
export interface AuthContextValue {
  isAuthenticated: boolean
  user: UserInfo | null
  login: (email: string, password: string) => Promise<void>
  googleLogin: (token: string) => Promise<void>
  magicLinkLogin: (token: string, redirect: string) => Promise<void>
  logout: () => Promise<void>
  /** Verify session from cookies - used for cross-tab sync */
  verifySession: () => Promise<boolean>
  /** Clear auth state without calling logout API - used for cross-tab sync */
  clearSession: () => void
  /** Access token for API calls (stored in memory) */
  token: string | undefined
  roles: string[]
  isLoading: boolean
  error: string | null
}

// Re-export UserInfo for convenience
export type { UserInfo }

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Authentication provider component
 *
 * Stores access token in memory (not localStorage) for better security.
 * Uses HTTP-only cookies for refresh token.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient()
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null)
  const [user, setUser] = useState<UserInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Ref to track if we're currently refreshing (prevent concurrent refreshes)
  const isRefreshing = useRef(false)
  // Ref for refresh timer
  const refreshTimerRef = useRef<number | null>(null)

  /**
   * Refresh the access token using HTTP-only cookie
   */
  const doRefresh = useCallback(async (): Promise<boolean> => {
    if (isRefreshing.current) {
      return false
    }

    isRefreshing.current = true
    try {
      const response = await apiRefreshToken()
      setAccessToken(response.access_token)
      const expiry = Date.now() + response.expires_in * 1000
      setTokenExpiry(expiry)

      // Update user info from new token
      const payload = decodeJwtPayload(response.access_token)
      if (payload) {
        setUser(extractUserInfo(payload))
      }

      return true
    } catch (err) {
      console.error('Token refresh failed:', err)
      // Clear state on refresh failure
      setAccessToken(null)
      setTokenExpiry(null)
      setUser(null)
      return false
    } finally {
      isRefreshing.current = false
    }
  }, [])

  /**
   * Schedule token refresh before expiry
   */
  const scheduleRefresh = useCallback(
    (expiry: number) => {
      // Clear any existing timer
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current)
      }

      // Refresh 60 seconds before expiry
      const refreshTime = expiry - Date.now() - 60000
      if (refreshTime > 0) {
        refreshTimerRef.current = window.setTimeout(() => {
          doRefresh()
        }, refreshTime)
      } else if (refreshTime > -60000) {
        // Token is about to expire, refresh immediately
        doRefresh()
      }
    },
    [doRefresh]
  )

  /**
   * Try to restore session on mount using cookies
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Get current session from cookies - returns access token for in-memory storage
        const response = await getCurrentSession()
        setAccessToken(response.access_token)
        const expiry = Date.now() + response.expires_in * 1000
        setTokenExpiry(expiry)
        setUser(response.user)
        scheduleRefresh(expiry)
      } catch {
        // No valid session - this is expected for new users
        setAccessToken(null)
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current)
      }
    }
  }, [scheduleRefresh])

  /**
   * Schedule refresh when tokenExpiry changes
   */
  useEffect(() => {
    if (tokenExpiry) {
      scheduleRefresh(tokenExpiry)
    }
  }, [tokenExpiry, scheduleRefresh])

  /**
   * Verify session from cookies - used for cross-tab sync
   * Returns true if session was restored, false otherwise
   */
  const verifySession = useCallback(async (): Promise<boolean> => {
    try {
      const response = await getCurrentSession()
      setAccessToken(response.access_token)
      const expiry = Date.now() + response.expires_in * 1000
      setTokenExpiry(expiry)
      setUser(response.user)
      scheduleRefresh(expiry)
      return true
    } catch {
      // Session is invalid - clear local state
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current)
      }
      setAccessToken(null)
      setTokenExpiry(null)
      setUser(null)
      setError(null)
      return false
    }
  }, [scheduleRefresh])

  /**
   * Clear auth state without calling logout API - used for cross-tab sync
   */
  const clearSession = useCallback(() => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current)
    }
    // Clear TanStack Query cache to prevent data leakage between users
    queryClient.clear()
    setAccessToken(null)
    setTokenExpiry(null)
    setUser(null)
    setError(null)
  }, [queryClient])

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (email: string, password: string) => {
      // Note: We don't set isLoading here because that would unmount the router
      // (see RoutedApp in App.tsx). The LoginPage has its own isSubmitting state.
      setError(null)

      try {
        const response = await apiLogin(email, password)

        // Record the sign-in method for the Sign-In Completed event.
        // The event itself fires later, from the identify lifecycle, once the
        // no-track gate is open. Only the method enum is recorded — no PII.
        recordSignInMethod('password')

        // Store access token in memory
        setAccessToken(response.access_token)
        const expiry = Date.now() + response.expires_in * 1000
        setTokenExpiry(expiry)
        setUser(response.user)
        scheduleRefresh(expiry)
      } catch (err) {
        if (err instanceof AuthServiceError) {
          setError(err.message)
        } else {
          setError('An unexpected error occurred')
        }
        throw err
      }
    },
    [scheduleRefresh]
  )

  /**
   * Login with Google ID token
   */
  const googleLogin = useCallback(
    async (token: string) => {
      // Note: We don't set isLoading here because that would unmount the router
      // (see RoutedApp in App.tsx). The LoginPage has its own loading state.
      setError(null)

      try {
        const response = await apiGoogleLogin(token)

        // Record the sign-in method (see `login` above).
        recordSignInMethod('google')

        // Store access token in memory
        setAccessToken(response.access_token)
        const expiry = Date.now() + response.expires_in * 1000
        setTokenExpiry(expiry)
        setUser(response.user)
        scheduleRefresh(expiry)
      } catch (err) {
        if (err instanceof AuthServiceError) {
          setError(err.message)
        } else {
          setError('An unexpected error occurred')
        }
        throw err
      }
    },
    [scheduleRefresh]
  )

  /**
   * Login by exchanging a magic-link token. Mirrors googleLogin's flow: the
   * server returns the same LoginResponse shape, so we just stash the access
   * token and user info and schedule the refresh.
   */
  const magicLinkLogin = useCallback(
    async (token: string, redirect: string) => {
      setError(null)

      try {
        const response = await apiMagicLinkLogin(token, redirect)

        // Record the sign-in method (see `login` above). This survives
        // the magic-link page's `window.location.assign` hard reload via
        // sessionStorage, so the event fires from the identify lifecycle on the
        // post-reload page load.
        recordSignInMethod('magic_link')

        setAccessToken(response.access_token)
        const expiry = Date.now() + response.expires_in * 1000
        setTokenExpiry(expiry)
        setUser(response.user)
        scheduleRefresh(expiry)
      } catch (err) {
        if (err instanceof AuthServiceError) {
          setError(err.message)
        } else {
          setError('An unexpected error occurred')
        }
        throw err
      }
    },
    [scheduleRefresh]
  )

  /**
   * Logout and clear session
   */
  const logout = useCallback(async () => {
    // Clear refresh timer
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current)
    }

    // Call backend to clear cookies
    await apiLogout()

    // Clear TanStack Query cache to prevent data leakage between users
    queryClient.clear()

    // Clear local state
    setAccessToken(null)
    setTokenExpiry(null)
    setUser(null)
    setError(null)
  }, [queryClient])

  const token = accessToken || undefined
  const roles = useMemo(() => user?.roles || [], [user])

  const value: AuthContextValue = useMemo(
    () => ({
      isAuthenticated: !!accessToken && !!user,
      user,
      login,
      googleLogin,
      magicLinkLogin,
      logout,
      verifySession,
      clearSession,
      token,
      roles,
      isLoading,
      error,
    }),
    [
      accessToken,
      user,
      login,
      googleLogin,
      magicLinkLogin,
      logout,
      verifySession,
      clearSession,
      token,
      roles,
      isLoading,
      error,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to access auth context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

// Helper functions for JWT decoding

interface JwtPayload {
  sub?: string
  email?: string
  preferred_username?: string
  given_name?: string
  family_name?: string
  name?: string
  email_verified?: boolean
  roles?: string[]
}

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

function extractUserInfo(payload: JwtPayload): UserInfo {
  const roles = (payload.roles || []).filter(
    (role: string) => !['offline_access', 'uma_authorization'].includes(role)
  )

  return {
    user_id: payload.sub || '',
    email: payload.email || '',
    username: payload.preferred_username || payload.email || '',
    roles,
    given_name: payload.given_name,
    family_name: payload.family_name,
    full_name: payload.name,
    email_verified: payload.email_verified || false,
  }
}
